import {
  RefObject, useCallback, useEffect, useState,
} from 'react';

export const SCENARIO_ROTATION_DURATION = 10000;

const useScenarioRotation = (containerRef: RefObject<HTMLElement>, count: number) => {
  const [{ active, cycle }, setSelection] = useState({ active: 0, cycle: 0 });
  const [running, setRunning] = useState(false);

  const select = useCallback((index: number) => {
    setSelection((current) => ({ active: index, cycle: current.cycle + 1 }));
  }, []);

  const restart = useCallback(() => {
    setSelection((current) => ({ ...current, cycle: current.cycle + 1 }));
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    setRunning(false);
    if (!container || count < 2) {
      return undefined;
    }

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let inView = false;
    let remaining = SCENARIO_ROTATION_DURATION;
    let started = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const update = () => {
      const shouldRun = inView && !document.hidden && !motion.matches;
      setRunning(shouldRun);
      if (!shouldRun && timer !== undefined) {
        remaining = Math.max(0, remaining - (performance.now() - started));
        clearTimeout(timer);
        timer = undefined;
      } else if (shouldRun && timer === undefined) {
        started = performance.now();
        timer = setTimeout(() => {
          timer = undefined;
          setSelection((current) => ({ active: (current.active + 1) % count, cycle: current.cycle + 1 }));
        }, remaining);
      }
    };

    const observer = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      update();
    }, { threshold: 0.15 });

    observer.observe(container);
    motion.addEventListener('change', update);
    document.addEventListener('visibilitychange', update);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      motion.removeEventListener('change', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, [containerRef, count, cycle]);

  return {
    active, cycle, running, select, restart,
  };
};

export default useScenarioRotation;
